import { type FormEvent, useRef, useState } from "react";

import AppShell, { EmptyState, ErrorNote } from "@/components/AppShell";
import { api, scopedPath } from "@/lib/api";
import { formatDate, useAsync } from "@/lib/hooks";
import type { PhotoItem, PhotoList } from "@/types";

export default function Gallery() {
  const { data, error, loading, reload } = useAsync<PhotoList>(
    () => api.get<PhotoList>("/api/photos"),
    [],
  );
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState<PhotoItem | null>(null);

  return (
    <AppShell
      title="Photos"
      subtitle={data ? `${data.total} photo${data.total === 1 ? "" : "s"}` : undefined}
      action={
        <button
          type="button"
          onClick={() => setUploading(true)}
          className="btn btn-primary px-3 py-2 text-sm"
        >
          Add
        </button>
      }
    >
      {error && <ErrorNote message={error} />}
      {loading && <p className="text-sm text-(--color-ink-muted)">Loading…</p>}

      {data && data.items.length === 0 && (
        <EmptyState
          title="No photos yet"
          body="Model numbers, plumbing runs, the liner pattern — the things you'll wish you'd photographed when you're on the phone to a supplier."
          action={
            <button type="button" onClick={() => setUploading(true)} className="btn btn-primary">
              Add a photo
            </button>
          }
        />
      )}

      {data && data.items.length > 0 && (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {data.items.map((photo) => (
            <li key={photo.id}>
              <button
                type="button"
                onClick={() => setSelected(photo)}
                className="card w-full overflow-hidden text-left"
              >
                <img
                  src={scopedPath(`/api/photos/${photo.id}/thumb`)}
                  alt={photo.label}
                  loading="lazy"
                  className="aspect-square w-full object-cover"
                />
                <div className="p-2">
                  <p className="truncate text-xs font-medium">{photo.label}</p>
                  <p className="unit mt-0.5">
                    {formatDate(photo.taken_at ?? photo.uploaded_at)}
                  </p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {uploading && (
        <UploadDialog
          onClose={() => setUploading(false)}
          onUploaded={() => {
            setUploading(false);
            reload();
          }}
        />
      )}

      {selected && (
        <PhotoDialog
          photo={selected}
          onClose={() => setSelected(null)}
          onDeleted={() => {
            setSelected(null);
            reload();
          }}
        />
      )}
    </AppShell>
  );
}

function UploadDialog({
  onClose,
  onUploaded,
}: {
  onClose: () => void;
  onUploaded: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) return;

    setBusy(true);
    setError(null);
    const form = new FormData();
    form.append("file", file);
    form.append("label", label || file.name);
    if (description) form.append("description", description);
    if (tags) form.append("tags", tags);

    try {
      await api.post("/api/photos", form);
      onUploaded();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not upload that photo.");
      setBusy(false);
    }
  }

  return (
    <Dialog title="Add a photo" onClose={onClose}>
      <form onSubmit={submit} className="mt-3 space-y-3">
        <input
          ref={fileRef}
          type="file"
          // HEIC is accepted explicitly: iPhones shoot it by default, and the
          // server transcodes it to JPEG on the way in.
          accept="image/*,.heic,.heif"
          required
          className="field"
        />
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Label — e.g. Filter data plate"
          className="field"
        />
        <textarea
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          className="field resize-y"
        />
        <input
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="Tags, comma separated (optional)"
          className="field"
        />
        {error && <ErrorNote message={error} />}
        <button type="submit" disabled={busy} className="btn btn-primary w-full">
          {busy ? "Uploading…" : "Upload"}
        </button>
      </form>
    </Dialog>
  );
}

function PhotoDialog({
  photo,
  onClose,
  onDeleted,
}: {
  photo: PhotoItem;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  async function remove() {
    setBusy(true);
    try {
      await api.delete(`/api/photos/${photo.id}`);
      onDeleted();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={photo.label} onClose={onClose}>
      <img
        src={scopedPath(`/api/photos/${photo.id}/file`)}
        alt={photo.label}
        className="mt-3 w-full rounded-xl"
      />
      {photo.description && <p className="mt-3 text-sm">{photo.description}</p>}

      <dl className="mt-3 space-y-1 text-xs text-(--color-ink-muted)">
        {photo.taken_at && (
          <div>
            <dt className="inline">Taken </dt>
            <dd className="inline">{formatDate(photo.taken_at)}</dd>
          </div>
        )}
        <div>
          <dt className="inline">Uploaded </dt>
          <dd className="inline">{formatDate(photo.uploaded_at)}</dd>
        </div>
        {photo.tags.length > 0 && (
          <div>
            <dt className="inline">Tags </dt>
            <dd className="inline">{photo.tags.join(", ")}</dd>
          </div>
        )}
      </dl>

      {confirming ? (
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={remove}
            disabled={busy}
            className="btn flex-1 bg-(--color-status-high) text-white"
          >
            {busy ? "Deleting…" : "Delete"}
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className="btn btn-quiet flex-1"
          >
            Keep it
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="mt-4 text-sm text-(--color-ink-muted) underline underline-offset-4"
        >
          Delete this photo
        </button>
      )}
    </Dialog>
  );
}

function Dialog({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="max-h-[88dvh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-(--color-surface-raised) p-5 sm:rounded-2xl">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="truncate text-lg font-semibold">{title}</h2>
          <button type="button" onClick={onClose} className="text-sm text-(--color-ink-muted)">
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
